import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ExternalLink, FolderPlus, FileText, Loader2 } from 'lucide-react';
import { type ChatAttachmentRef } from '@/lib/api';

interface FileViewerModalProps {
  attachment: ChatAttachmentRef;
  /** Inline-disposition URL — renders in-page rather than forcing a download. */
  url: string;
  onClose: () => void;
  /** Save-to-device (fetches a fresh attachment-disposition URL upstream). */
  onDownload: () => void;
  /** Save into the user's Fias files. Omitted for legacy vault-backed refs. */
  onSaveToVault?: () => void;
  savingToVault?: boolean;
}

type PreviewKind = 'image' | 'video' | 'audio' | 'pdf' | 'text';

function previewKind(mime: string): PreviewKind | null {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  // The plain-text family renders safely in an iframe. `text/html` is excluded
  // on purpose — it would execute as a page — and falls through to the
  // no-preview panel (open-in-new-tab / download / save instead).
  if (mime.startsWith('text/') && mime !== 'text/html') return 'text';
  return null;
}

export function FileViewerModal({
  attachment,
  url,
  onClose,
  onDownload,
  onSaveToVault,
  savingToVault,
}: FileViewerModalProps) {
  const kind = previewKind(attachment.mimeType);

  // Close on Escape
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handle, true);
    return () => document.removeEventListener('keydown', handle, true);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center animate-in fade-in-0 duration-150"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80" />

      {/* Content — stop propagation so clicking media doesn't close modal */}
      <div
        className="relative z-10 flex flex-col items-center max-w-[92vw] max-h-[92vh] animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-10 right-0 p-2 text-white/70 hover:text-white transition-colors"
          title="Close (Esc)"
        >
          <X className="h-5 w-5" />
        </button>

        {kind === 'image' && (
          <img
            src={url}
            alt={attachment.fileName}
            className="max-w-[92vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
            draggable={false}
          />
        )}

        {kind === 'video' && (
          <video
            src={url}
            controls
            autoPlay
            className="max-w-[92vw] max-h-[80vh] rounded-lg shadow-2xl"
          />
        )}

        {kind === 'audio' && (
          <div className="bg-white rounded-lg p-6 shadow-2xl min-w-[320px]">
            <audio src={url} controls autoPlay className="w-full" />
          </div>
        )}

        {(kind === 'pdf' || kind === 'text') && (
          // Cross-origin (S3) src — isolated from this origin by same-origin policy.
          <iframe
            src={url}
            title={attachment.fileName}
            className="w-[85vw] h-[80vh] rounded-lg shadow-2xl bg-white"
          />
        )}

        {!kind && (
          <div className="bg-white rounded-lg p-8 shadow-2xl flex flex-col items-center gap-3 min-w-[320px] max-w-[90vw]">
            <FileText className="h-12 w-12 text-foreground/30" />
            <p className="text-sm text-foreground/70 text-center">
              Preview isn&apos;t available for this file type.
            </p>
          </div>
        )}

        {/* Filename + actions */}
        <div className="mt-3 flex flex-col items-center gap-2">
          <p className="text-sm text-white/60 truncate max-w-[60vw]">{attachment.fileName}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open in new tab
            </button>
            <button
              type="button"
              onClick={onDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
            {onSaveToVault && (
              <button
                type="button"
                onClick={onSaveToVault}
                disabled={savingToVault}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                {savingToVault ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FolderPlus className="h-3.5 w-3.5" />
                )}
                Save to Fias files
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
